import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RepositoryBase } from '@asm/features/sync/repositories/repository-base';
import { LocalOrderHistoryItem } from '@asm/features/sync/models/order.model';

@Injectable({ providedIn: 'root' })
export class OrderHistoryRepository extends RepositoryBase<LocalOrderHistoryItem> {
  protected readonly KEY_PATH = 'order_history';

  /**
   * Retorna todos os itens de um b2bUnit + customer filtrados por isQuotation,
   * ordenados por createdAt decrescente.
   */
  getByUnitAndCustomer(
    b2bUnitUid: string,
    customerUid: string,
    isQuotation: boolean,
  ): Observable<LocalOrderHistoryItem[]> {
    return this.getAll().pipe(
      map(items =>
        items
          .filter(i => i.b2bUnitUid === b2bUnitUid && i.customerUid === customerUid && i.isQuotation === isQuotation)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      ),
    );
  }

  /** Busca por Nº Pedido ou Ordem de Venda (case-insensitive). */
  search(
    b2bUnitUid: string,
    customerUid: string,
    isQuotation: boolean,
    query: string,
  ): Observable<LocalOrderHistoryItem[]> {
    const q = query.toLowerCase();
    return this.getByUnitAndCustomer(b2bUnitUid, customerUid, isQuotation).pipe(
      map(items =>
        items.filter(i => i.orderCode.toLowerCase().includes(q) || (i.erpOrderCode ?? '').toLowerCase().includes(q)),
      ),
    );
  }

  /**
   * Retorna o primeiro registro de um pedido pelo seu orderCode.
   * Usado pela tela de detalhe do pedido (online e offline via OrderOfflineHandler).
   */
  getByOrderCode(b2bUnitUid: string, customerUid: string, orderCode: string): Observable<LocalOrderHistoryItem | null> {
    return this.getAll().pipe(
      map(items => {
        const match = items.find(
          i => i.b2bUnitUid === b2bUnitUid && i.customerUid === customerUid && i.orderCode === orderCode,
        );
        return match ?? null;
      }),
    );
  }

  /**
   * Returns all locally created orders that are still waiting to be sent to the server.
   * Replaces OfflineOrderRepository.getPending() — order_history is the single source.
   */
  getPending(): Observable<LocalOrderHistoryItem[]> {
    return this.getAll().pipe(map(items => items.filter(i => i.isLocalOrder && i.localSyncStatus === 'PENDING')));
  }

  /** Updates the sync status of a local order (by localId). */
  updateSyncStatus(
    localId: string,
    status: 'PENDING' | 'SYNCED' | 'ERROR',
    errorMessage?: string,
  ): Observable<LocalOrderHistoryItem> {
    return new Observable(observer => {
      this.getByKey(localId).subscribe({
        next: item => {
          if (!item) {
            observer.complete();
            return;
          }
          const updated: LocalOrderHistoryItem = {
            ...item,
            localSyncStatus: status,
            syncErrorMessage: errorMessage,
          };
          this.addOrUpdate(updated).subscribe({
            next: result => {
              observer.next(result);
              observer.complete();
            },
            error: err => observer.error(err),
          });
        },
        error: err => observer.error(err),
      });
    });
  }
}
